/**
 * UpdatesBlockingGate — full-screen block on app open for unacknowledged
 * "I Understand" updates. The user must acknowledge each before navigating.
 */
import { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../lib/apiFetch';
import { UpdateBody, CategoryChip, notifyUpdatesChanged } from './UpdatesNoticesPanel';

const GOLD = '#C9A962';

export default function UpdatesBlockingGate() {
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/updates/blocking');
      setQueue(d.updates || []);
    } catch (_) { setQueue([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (queue.length === 0) return null;
  const current = queue[0];

  const acknowledge = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/updates/${current.update_id}/acknowledge`, { method: 'POST' });
      toast.success('Acknowledged');
      notifyUpdatesChanged();
      setQueue((q) => q.slice(1));
    } catch (e) {
      toast.error(e.detail || 'Failed — please try again');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 overflow-y-auto"
      style={{ backgroundColor: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(6px)' }}
      data-testid="updates-blocking-gate">
      <div className="w-full max-w-lg rounded-3xl border p-5 my-auto"
        style={{ borderColor: GOLD, backgroundColor: '#141414' }}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="h-5 w-5 text-red-500" />
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-red-400">
            Acknowledgment Required{queue.length > 1 && ` · ${queue.length} pending`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryChip update={current} />
          <span className="flex items-center gap-1 text-[10px] text-zinc-500">
            <CalendarDays className="h-3 w-3" /> Effective {current.effective_date}
          </span>
        </div>
        <h2 className="text-xl font-black text-white mt-2 break-words"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="blocking-update-title">
          {current.title}
        </h2>
        <div className="mt-3 max-h-[45vh] overflow-y-auto pr-1">
          <UpdateBody update={current} />
        </div>
        <button onClick={acknowledge} disabled={busy}
          className="w-full rounded-xl py-3.5 mt-5 text-sm font-black uppercase tracking-wider disabled:opacity-60"
          style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
          data-testid="blocking-understand-btn">
          I Understand
        </button>
        <p className="text-[10px] text-zinc-600 text-center mt-2">
          You must acknowledge this update before continuing.
        </p>
      </div>
    </div>
  );
}
